import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import SiteLayout from "@/components/SiteLayout";
import PageLoader from "@/components/PageLoader";

import Home from "@/pages/Home";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Portfolio from "@/pages/Portfolio";
import PortfolioDetail from "@/pages/PortfolioDetail";
import About from "@/pages/About";
import Book from "@/pages/Book";
import MyBookings from "@/pages/MyBookings";
import StripeReturn from "@/pages/StripeReturn";
import PayPalReturn from "@/pages/PayPalReturn";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import SmsConsent from "@/pages/SmsConsent";
import Review from "@/pages/Review";
import Reviews from "@/pages/admin/Reviews";
import TextMessages from "@/pages/admin/TextMessages";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import AdminCalendar from "@/pages/admin/AdminCalendar";
import Availability from "@/pages/admin/Availability";
import StudioHours from "@/pages/admin/StudioHours";
import ManageContent from "@/pages/admin/ManageContent";
import Equipment from "@/pages/admin/Equipment";
import Promos from "@/pages/admin/Promos";
import AboutEditor from "@/pages/admin/AboutEditor";
import Customers from "@/pages/admin/Customers";
import SuperAdmin from "@/pages/admin/SuperAdmin";

function Protected({ children }) {
  const { admin, checked } = useAuth();
  if (!checked) return <PageLoader />;
  if (!admin) return <Navigate to="/admin/login" replace />;
  return children;
}

function SuperOnly({ children }) {
  const { admin } = useAuth();
  if (admin?.role !== "super_admin") return <Navigate to="/admin" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route element={<SiteLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/portfolio/:slug" element={<PortfolioDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/book" element={<Book />} />
            <Route path="/my-bookings" element={<MyBookings />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/sms-consent" element={<SmsConsent />} />
            <Route path="/review" element={<Review />} />
            <Route path="/stripe/return" element={<StripeReturn />} />
            <Route path="/paypal/return" element={<PayPalReturn />} />
          </Route>

          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <Protected>
                <AdminLayout />
              </Protected>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="calendar" element={<AdminCalendar />} />
            <Route path="availability" element={<Availability />} />
            <Route path="studio-hours" element={<StudioHours />} />
            <Route path="content" element={<ManageContent />} />
            <Route path="equipment" element={<SuperOnly><Equipment /></SuperOnly>} />
            <Route path="promos" element={<SuperOnly><Promos /></SuperOnly>} />
            <Route path="text-messages" element={<SuperOnly><TextMessages /></SuperOnly>} />
            <Route path="about" element={<AboutEditor />} />
            <Route path="customers" element={<Customers />} />
            <Route path="reviews" element={<Reviews />} />
            <Route path="super" element={<SuperOnly><SuperAdmin /></SuperOnly>} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
